/**
 * Proposal state for household meal planning.
 *
 * Lives in a .ts rather than beside the cards for the same reasons as
 * approvalState.ts and cookState.ts: tsconfig emits `jsx: preserve`,
 * so Jest can never import from a .tsx.
 */
import { MAX_PROPOSALS_PER_SLOT, type PlannedMeal } from '../../redux/mealPlanSlice';

export type HouseholdStatus = 'solo' | 'pending' | 'paired';

/**
 * The proposals in a slot, truncated to MAX_PROPOSALS_PER_SLOT.
 * Two offline members can both land a proposal past the cap; since repairing
 * on write would need a transaction, the cap is honoured on read instead.
 * Truncation is deterministic (oldest first) so both members see the same two.
 */
export function proposalsOf(slotMeals: PlannedMeal[]): PlannedMeal[] {
  const proposals = slotMeals.filter((m) => m.mode === 'proposed');

  // Sort by createdAt to ensure deterministic truncation
  if (proposals.length > 1) {
    proposals.sort((a, b) => {
      if (!a.createdAt && !b.createdAt) return 0;
      if (!a.createdAt) return 1;
      if (!b.createdAt) return -1;
      return a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0;
    });
  }

  // Keep only the oldest MAX_PROPOSALS_PER_SLOT (in case of offline collision)
  return proposals.slice(0, MAX_PROPOSALS_PER_SLOT);
}

/**
 * Does the slot hold at least one accepted meal?
 * Used to determine if a slot should render an empty state.
 */
export function slotHasAcceptedMeal(slotMeals: PlannedMeal[]): boolean {
  return slotMeals.some((m) => m.mode !== 'proposed');
}

/**
 * Can another proposal be added to this slot?
 * False when the slot already holds MAX_PROPOSALS_PER_SLOT proposals,
 * or when the household is not in 'paired' status.
 */
export interface CanProposeInput {
  slotMeals: PlannedMeal[];
  status: HouseholdStatus;
}

export function canProposeToSlot(input: CanProposeInput): boolean {
  const { slotMeals, status } = input;

  // Only paired households can propose
  if (status !== 'paired') return false;

  // Check if slot already has MAX_PROPOSALS_PER_SLOT proposals
  const currentProposals = proposalsOf(slotMeals);
  return currentProposals.length < MAX_PROPOSALS_PER_SLOT;
}

/**
 * Can the current user remove this proposal?
 *
 * Removal rules (§4 product decision):
 * - The **author** can always remove their own proposal.
 * - The **assigned cook** can remove **any** proposal.
 * - **While no cook is assigned, neither member may remove the other's proposal.**
 *   Taking the meal on is what earns the right to clear someone else's idea.
 */
export interface CanRemoveProposalInput {
  proposal: PlannedMeal;
  myUid: string | null;
  cookId: string | undefined;
}

export function canRemoveProposal(input: CanRemoveProposalInput): boolean {
  const { proposal, myUid, cookId } = input;

  if (!myUid) return false;

  // The author can always remove their own proposal
  if (proposal.createdByUid === myUid) return true;

  // The cook can remove any proposal
  if (cookId === myUid) return true;

  // While no cook is assigned, neither member may remove the other's proposal
  return false;
}

/**
 * Can the current user accept a proposal (promote it to the menu)?
 *
 * Acceptance rules (§3 product decision):
 * - The **assigned cook decides**; if nobody is assigned, **either member may**.
 * - Whoever chooses becomes the cook.
 *
 * So permission to accept is true when there is no cookId, or when cookId === myUid.
 */
export interface CanAcceptProposalInput {
  myUid: string | null;
  cookId: string | undefined;
}

export function canAcceptProposal(input: CanAcceptProposalInput): boolean {
  const { myUid, cookId } = input;

  // If no cook is assigned, either member can accept
  if (cookId === undefined) return true;

  // If a cook is assigned, only that cook can accept
  return cookId === myUid;
}
